(function () {
  'use strict';

  var ALLOWED_TAGS = new Set(['A', 'B', 'BR', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'U', 'UL']);
  var ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'title']);
  var LEGACY_SECTION_KEYS = {
    pdp_title: 'title',
    pdp_intro: 'intro_text',
    trust_text: 'reviews_label',
    trust_subtext: 'reviews_subtext',
    pdp_cta_label: 'add_to_cart_text'
  };
  var RESERVED_CAMPAIGN_KEYS = new Set([
    'id', 'gid', 'slug', 'name', 'enabled', 'status', 'product', 'product_id',
    'product_handle', 'created_at', 'updated_at', 'seo', 'section', 'blocks',
    'block_overrides', 'content_blocks', 'content', 'fields', 'settings', 'styles',
    'pdp_image', 'pdp_mobile_image', 'meta_title', 'meta_description'
  ]);
  var SECTION_SELECTORS = {
    title: ['[data-dlp-setting="title"]', '.exseed-pdp-shell__title'],
    intro_text: ['[data-dlp-setting="intro_text"]', '.exseed-pdp-shell__intro'],
    reviews_label: ['[data-dlp-setting="reviews_label"]', '.exseed-pdp-shell__stars > span'],
    reviews_subtext: ['[data-dlp-setting="reviews_subtext"]', '.exseed-pdp-shell__reviews-sub'],
    variant_label: ['[data-dynamic-variant-label]'],
    only_label: ['[data-dynamic-only-label]'],
    per_test_suffix: ['[data-dynamic-per-test-suffix]'],
    one_time_purchase_label: ['[data-dynamic-one-time-label]'],
    subscription_heading: ['[data-dynamic-subscription-heading]'],
    subscription_intro: ['[data-subscription-intro]'],
    subscription_usp_1: ['[data-dynamic-subscription-usp="1"] [data-dynamic-copy]'],
    subscription_usp_2: ['[data-dynamic-subscription-usp="2"] [data-dynamic-copy]'],
    subscription_usp_3: ['[data-dynamic-subscription-usp="3"] [data-dynamic-copy]'],
    subscription_usp_4: ['[data-dynamic-subscription-usp="4"] [data-dynamic-copy]'],
    subscription_footer: ['[data-dynamic-subscription-footer]'],
    add_to_cart_text: ['[data-add-to-cart-label]'],
    sold_out_text: ['[data-add-to-cart-label][data-sold-out]']
  };
  var STYLE_PROPERTIES = {
    shell_bg_mobile: '--shell-bg-mobile', shell_bg_desktop: '--shell-bg-desktop',
    hero_card_bg: '--hero-card-bg', text_color: '--text-color', muted_color: '--muted-color',
    heading_color: '--heading-color', border_color: '--border-color', active_color: '--active-color',
    active_text_color: '--active-text-color', save_bg: '--save-bg', save_text: '--save-text',
    button_bg: '--button-bg', button_text: '--button-text', media_radius: '--media-radius',
    panel_radius: '--panel-radius', button_radius: '--button-radius', desktop_gap: '--desktop-gap',
    heading_size_mobile: '--heading-size-mobile', heading_size_desktop: '--heading-size-desktop',
    body_size_mobile: '--body-size-mobile', body_size_desktop: '--body-size-desktop',
    label_size_mobile: '--label-size-mobile', label_size_desktop: '--label-size-desktop',
    price_size_mobile: '--price-size-mobile', price_size_desktop: '--price-size-desktop',
    heading_weight: '--heading-weight', body_weight: '--body-weight', button_weight: '--button-weight'
  };
  var BUYBOX_DATA_SETTINGS = {
    subscription_button_text: 'data-dlp-subscription-button-text',
    subscription_discount_suffix: 'data-dlp-subscription-discount-suffix',
    subscription_default_discount: 'data-dlp-subscription-default-discount',
    only_label: 'data-dlp-only-label',
    per_test_suffix: 'data-dlp-per-test-suffix'
  };

  function normalise(value) {
    return String(value == null ? '' : value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function toSnakeCase(value) {
    return String(value || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  function canonicalSettingKey(key) {
    var normalisedKey = toSnakeCase(key);
    return LEGACY_SECTION_KEYS[normalisedKey] || normalisedKey;
  }

  function asObject(value) {
    if (value && typeof value === 'object') return value;
    if (typeof value === 'string' && value.trim()) {
      try {
        var parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }
    return {};
  }

  function firstPopulatedObject(values) {
    for (var i = 0; i < values.length; i += 1) {
      var candidate = asObject(values[i]);
      if (Array.isArray(candidate) ? candidate.length : Object.keys(candidate).length) return candidate;
    }
    return {};
  }

  function asOverride(value, fallbackType) {
    if (value == null) return null;
    if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) return value;
    if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'html')) {
      return { type: 'richtext', value: value.html, original: value.original || '' };
    }
    if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'text')) {
      return { type: fallbackType || 'text', value: value.text, original: value.original || '' };
    }
    return { type: fallbackType || 'text', value: value, original: '' };
  }

  function overrideValue(value) {
    var override = asOverride(value);
    return override ? override.value : null;
  }

  function asBoolean(value) {
    value = overrideValue(value);
    if (typeof value === 'string') {
      var normalisedValue = value.trim().toLowerCase();
      if (normalisedValue === 'false' || normalisedValue === '0' || normalisedValue === 'no' || normalisedValue === 'off') return false;
      if (normalisedValue === 'true' || normalisedValue === '1' || normalisedValue === 'yes' || normalisedValue === 'on') return true;
    }
    return Boolean(value);
  }

  function safeUrl(value) {
    var url = String(value || '').trim();
    if (!url) return '';
    if (/^(https?:|mailto:|tel:|\/|#)/i.test(url)) return url;
    return '';
  }

  function sanitiseHtml(html) {
    var wrapper = document.createElement('div');
    wrapper.innerHTML = String(html || '');
    Array.prototype.slice.call(wrapper.querySelectorAll('*')).forEach(function (node) {
      if (!ALLOWED_TAGS.has(node.tagName)) {
        node.replaceWith(document.createTextNode(node.textContent || ''));
        return;
      }
      Array.prototype.slice.call(node.attributes).forEach(function (attribute) {
        if (!ALLOWED_ATTRS.has(attribute.name.toLowerCase())) node.removeAttribute(attribute.name);
      });
      if (node.tagName === 'A') {
        var href = safeUrl(node.getAttribute('href'));
        if (href) node.setAttribute('href', href); else node.removeAttribute('href');
        if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return wrapper.innerHTML;
  }

  function setNodeValue(node, override) {
    override = asOverride(override);
    if (!node || !override || override.value == null) return;
    if (typeof override.value === 'string' && override.value.trim() === '') return;
    var overrideType = toSnakeCase(override.type || 'text');
    if (overrideType === 'richtext' || overrideType === 'rich_text' || overrideType === 'html') {
      try {
        var safeHtml = sanitiseHtml(override.value);
        if (node.innerHTML !== safeHtml) node.innerHTML = safeHtml;
      } catch (error) {
        var fallbackText = normalise(override.value);
        if (node.textContent !== fallbackText) node.textContent = fallbackText;
        if (window.console && console.error) console.error('ExSeed DLP could not sanitise rich text; plain text was used.', error);
      }
    } else if (node.textContent !== String(override.value)) {
      node.textContent = String(override.value);
    }
    node.hidden = false;
  }

  function findTextElement(root, original) {
    var wanted = normalise(original);
    if (!wanted) return null;
    var nodes = root.querySelectorAll('p,h1,h2,h3,h4,h5,h6,span,li,a,button,summary,div');
    var matches = [];
    var containing = [];
    for (var i = 0; i < nodes.length; i += 1) {
      var current = normalise(nodes[i].textContent);
      if (current === wanted) matches.push(nodes[i]);
      else if (current && current.indexOf(wanted) !== -1) containing.push(nodes[i]);
    }
    if (!matches.length) matches = containing;
    matches.sort(function (a, b) {
      var childDifference = a.querySelectorAll('*').length - b.querySelectorAll('*').length;
      return childDifference || normalise(a.textContent).length - normalise(b.textContent).length;
    });
    return matches[0] || null;
  }

  function inferBlockType(blockId) {
    return String(blockId || '').replace(/_[^_]+$/, '');
  }

  function resolveBlock(shell, blocks, blockId, override) {
    var exact = shell.querySelector('[data-dlp-block-id="' + CSS.escape(blockId) + '"], [data-dlp-child-block-id="' + CSS.escape(blockId) + '"]');
    if (exact) return exact;

    var type = String(override && (override.type || override._type || override.blockType || override.block_type || override.shopifyType) || inferBlockType(blockId));
    if (!type) return null;
    var candidates = shell.querySelectorAll('[data-dlp-block-type="' + CSS.escape(type) + '"]');
    if (!candidates.length) return null;

    var typeIndexValue = override && (override.typeIndex != null ? override.typeIndex : (override._typeIndex != null ? override._typeIndex : override.type_index));
    var typeIndex = Number(typeIndexValue);
    if (!Number.isInteger(typeIndex) || typeIndex < 0) {
      var siblingIds = Object.keys(blocks || {}).filter(function (candidateId) {
        var candidate = blocks[candidateId] || {};
        return String(candidate.type || candidate._type || candidate.blockType || candidate.block_type || candidate.shopifyType || inferBlockType(candidateId)) === type;
      });
      typeIndex = Math.max(0, siblingIds.indexOf(blockId));
    }
    return candidates[Math.min(typeIndex, candidates.length - 1)] || null;
  }

  function applyGenericSetting(root, settingId, override) {
    override = asOverride(override);
    if (!override || override.value == null) return;
    var blockAttributeSettings = {
      button_text: ['.exseed-pdp-shell__left-block--email_form', 'data-email-button-text'],
      email_placeholder: ['.exseed-pdp-shell__left-block--email_form', 'data-email-placeholder'],
      text_placeholder: ['.exseed-pdp-shell__left-block--email_form', 'data-text-placeholder']
    };
    if (blockAttributeSettings[settingId] && String(override.value).trim() !== '') {
      var attributeRule = blockAttributeSettings[settingId];
      var attributeTarget = root.querySelector(attributeRule[0]);
      if (attributeTarget) {
        attributeTarget.setAttribute(attributeRule[1], String(override.value));
        document.dispatchEvent(new CustomEvent('exseed:dlp-content-updated'));
        return;
      }
    }
    var directLinks = root.querySelectorAll('[data-dlp-setting-link="' + CSS.escape(settingId) + '"]');
    if (directLinks.length) {
      var directUrl = safeUrl(override.value);
      if (directUrl) directLinks.forEach(function (node) { node.setAttribute('href', directUrl); });
      return;
    }
    var direct = root.querySelectorAll('[data-dlp-setting="' + CSS.escape(settingId) + '"]');
    if (direct.length) {
      direct.forEach(function (node) { setNodeValue(node, override); });
      return;
    }
    var type = override.type || 'text';
    if (type === 'url' || type === 'video_url') {
      var url = safeUrl(override.value);
      if (!url) return;
      var attr = type === 'video_url' ? 'src' : 'href';
      var candidates = root.querySelectorAll('[' + attr + ']');
      var original = String(override.original || '');
      for (var i = 0; i < candidates.length; i += 1) {
        if (!original || candidates[i].getAttribute(attr) === original) {
          candidates[i].setAttribute(attr, url);
          if (type === 'video_url' && candidates[i].load) candidates[i].load();
          return;
        }
      }
      return;
    }
    if (type === 'image_picker') {
      var images = root.querySelectorAll('img');
      var image = images[Math.max(0, Number(override.imageIndex || 0))];
      var imageUrl = safeUrl(override.value && override.value.url ? override.value.url : override.value);
      if (image && imageUrl) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
        if (override.alt || (override.value && override.value.alt)) image.alt = override.alt || override.value.alt;
      }
      return;
    }
    var match = findTextElement(root, override.original);
    if (match) setNodeValue(match, override);
  }

  function setAttribute(root, selector, attribute, override) {
    override = asOverride(override);
    if (!override || override.value == null || String(override.value).trim() === '') return;
    var value = attribute === 'href' ? safeUrl(override.value) : String(override.value);
    if (!value) return;
    root.querySelectorAll(selector).forEach(function (node) {
      if (node.getAttribute(attribute) !== value) node.setAttribute(attribute, value);
    });
  }

  function setVisibility(root, selector, override) {
    override = asOverride(override, 'checkbox');
    if (!override || override.value === '') return;
    root.querySelectorAll(selector).forEach(function (node) { node.hidden = !asBoolean(override); });
  }

  function applyBlockControl(target, settingId, override) {
    var normalisedOverride = asOverride(override, 'checkbox');
    if (!normalisedOverride) return false;
    var rules = {
      enable_mobile_background: '--block-mobile-bg',
      enable_mobile_border: '--block-mobile-border-width',
      enable_desktop_background: '--block-desktop-bg',
      enable_desktop_border: '--block-desktop-border-width',
      svg_cut_after_enable_mobile: '--svg-cut-after-mobile-display',
      svg_cut_after_enable_desktop: '--svg-cut-after-desktop-display'
    };
    if (!rules[settingId]) return false;
    var enabled = asBoolean(normalisedOverride);
    var disabledValue = settingId.indexOf('background') !== -1 ? 'transparent' : (settingId.indexOf('border') !== -1 ? '0px' : 'none');
    var enabledValue = settingId.indexOf('border') !== -1 ? '1px' : (settingId.indexOf('svg_cut') !== -1 ? 'block' : '');
    if (enabledValue === '') target.style.removeProperty(rules[settingId]);
    else target.style.setProperty(rules[settingId], enabled ? enabledValue : disabledValue);
    if (!enabled) target.style.setProperty(rules[settingId], disabledValue);
    return true;
  }

  function applySection(shell, section) {
    if (!section) return;
    Object.keys(section.styles || {}).forEach(function (key) {
      var canonicalKey = canonicalSettingKey(key);
      var property = STYLE_PROPERTIES[canonicalKey] || (key.indexOf('--') === 0 ? key : '');
      var value = overrideValue(section.styles[key]);
      if (property && value !== '') {
        if (/(_radius|_size_|_gap|padding|spacing)/.test(canonicalKey) && /^\d+(\.\d+)?$/.test(String(value))) value += 'px';
        shell.style.setProperty(property, String(value));
      }
    });
    Object.keys(section.settings || {}).forEach(function (key) {
      var override = section.settings[key];
      var canonicalKey = canonicalSettingKey(key);
      var inlineStyleProperty = STYLE_PROPERTIES[canonicalKey];
      if (inlineStyleProperty) {
        var inlineStyleValue = overrideValue(override);
        if (inlineStyleValue != null && inlineStyleValue !== '') {
          if (/(_radius|_size_|_gap|padding|spacing)/.test(canonicalKey) && /^\d+(\.\d+)?$/.test(String(inlineStyleValue))) inlineStyleValue += 'px';
          shell.style.setProperty(inlineStyleProperty, String(inlineStyleValue));
        }
      }
      var selectors = SECTION_SELECTORS[canonicalKey];
      if (selectors) selectors.forEach(function (selector) {
        shell.querySelectorAll(selector).forEach(function (node) { setNodeValue(node, override); });
      });
      if (canonicalKey === 'reviews_link') setAttribute(shell, '.jump-to-customer-reviews', 'href', override);
      if (canonicalKey === 'show_title') setVisibility(shell, '.exseed-pdp-shell__title', override);
      if (canonicalKey === 'show_reviews') setVisibility(shell, '.exseed-pdp-shell__reviews', override);
      if (canonicalKey === 'show_variant_selector') setVisibility(shell, '[data-dynamic-variant-label], .exseed-pdp-shell__options', override);
      if (canonicalKey === 'show_selected_title') setVisibility(shell, '[data-selected-title]', override);
      if (canonicalKey === 'show_payment_terms') setVisibility(shell, '.exseed-pdp-shell__payment-terms', override);
      if (BUYBOX_DATA_SETTINGS[canonicalKey]) {
        var dataValue = overrideValue(override);
        if (dataValue != null && String(dataValue).trim() !== '') {
          shell.querySelectorAll('[data-exseed-buybox]').forEach(function (buybox) {
            buybox.setAttribute(BUYBOX_DATA_SETTINGS[canonicalKey], String(dataValue));
          });
        }
      }
    });
    var image = window.matchMedia('(max-width: 749px)').matches && section.mobileImage ? section.mobileImage : section.image;
    image = overrideValue(image);
    var imageUrl = image && (image.url || image);
    if (imageUrl) {
      var mainImage = shell.querySelector('[data-carousel-track] .exseed-pdp-shell__slide:first-child img');
      if (mainImage) {
        mainImage.src = safeUrl(imageUrl) || mainImage.src;
        mainImage.removeAttribute('srcset');
        if (image.alt) mainImage.alt = image.alt;
      }
    }
  }

  function applyBlocks(shell, blocks, section) {
    section = section || {};
    Object.keys(blocks || {}).forEach(function (blockId) {
      var override = blocks[blockId] || {};
      var block = resolveBlock(shell, blocks, blockId, override);
      if (!block) return;
      var styleTarget = block.firstElementChild || block;
      if (override.visible != null && !asBoolean(override.visible)) { block.hidden = true; return; }
      block.hidden = false;
      if (override.order != null) block.style.order = String(override.order);
      var blockSettings = {};
      [override.content, override.fields, override.settings].forEach(function (source) {
        Object.keys(source || {}).forEach(function (settingId) { blockSettings[canonicalSettingKey(settingId)] = source[settingId]; });
      });
      Object.keys(override).forEach(function (settingId) {
        var reserved = ['type', '_type', 'blockType', 'block_type', 'shopifyType', 'typeIndex', '_typeIndex', 'type_index', 'visible', 'order', 'styles', 'content', 'fields', 'settings'];
        if (reserved.indexOf(settingId) === -1 && blockSettings[canonicalSettingKey(settingId)] == null) {
          blockSettings[canonicalSettingKey(settingId)] = override[settingId];
        }
      });
      Object.keys(blockSettings).forEach(function (settingId) {
        if (!applyBlockControl(styleTarget, settingId, blockSettings[settingId])) applyGenericSetting(block, settingId, blockSettings[settingId]);
      });
      Object.keys(override.styles || {}).forEach(function (property) {
        if (/^--[a-z0-9-]+$/i.test(property)) {
          var styleValue = overrideValue(override.styles[property]);
          if (styleValue === '') styleTarget.style.removeProperty(property);
          else if (styleValue != null) styleTarget.style.setProperty(property, String(styleValue));
        }
      });
    });

    var benefits = [1, 2, 3, 4].map(function (number) {
      var fallback = section.settings && section.settings['fallback_feature_' + number];
      var benefit = section.settings && section.settings['benefit_' + number];
      return overrideValue(fallback) || overrideValue(benefit) || '';
    }).filter(function (value) { return value != null && String(value).trim() !== ''; });
    if (benefits.length) shell.querySelectorAll('[data-selected-features]').forEach(function (container) {
      container.setAttribute('data-dlp-campaign-features', JSON.stringify(benefits));
      var rows = container.querySelectorAll('.exseed-pdp-shell__feature');
      benefits.forEach(function (benefit, index) {
        var row = rows[index];
        if (!row && rows[0]) { row = rows[0].cloneNode(true); container.appendChild(row); }
        var copy = row && row.querySelector('span:last-child');
        if (copy) copy.textContent = benefit;
      });
      Array.prototype.slice.call(rows, benefits.length).forEach(function (row) { row.remove(); });
      container.classList.remove('is-hidden');
    });
  }

  function applySeo(campaign) {
    var seo = campaign && campaign.seo ? campaign.seo : {};
    var title = String(overrideValue(seo.title || seo.metaTitle || seo.meta_title || campaign.meta_title || campaign.metaTitle || '') || '').trim();
    var description = String(overrideValue(seo.description || seo.metaDescription || seo.meta_description || campaign.meta_description || campaign.metaDescription || '') || '').trim();

    function upsertMeta(selector, attributes, value) {
      if (!value) return;
      var node = document.head.querySelector(selector);
      if (!node) {
        node = document.createElement('meta');
        Object.keys(attributes).forEach(function (key) { node.setAttribute(key, attributes[key]); });
        document.head.appendChild(node);
      }
      node.setAttribute('content', value);
    }

    if (title) {
      document.title = title;
      upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    }
    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description' }, description);
      upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    }
  }

  function normaliseCampaignSection(campaign) {
    var sourceSection = asObject(campaign.section);
    var section = {
      image: sourceSection.image || campaign.pdp_image || campaign.pdpImage || '',
      mobileImage: sourceSection.mobileImage || sourceSection.mobile_image || campaign.pdp_mobile_image || campaign.pdpMobileImage || '',
      settings: {},
      styles: Object.assign({}, campaign.styles || {}, sourceSection.styles || {})
    };
    Object.keys(campaign || {}).forEach(function (key) {
      var normalisedKey = toSnakeCase(key);
      if (!RESERVED_CAMPAIGN_KEYS.has(normalisedKey)) section.settings[canonicalSettingKey(key)] = campaign[key];
    });
    [campaign.content, campaign.fields, campaign.settings, sourceSection.settings].forEach(function (source) {
      source = asObject(source);
      Object.keys(source).forEach(function (key) { section.settings[canonicalSettingKey(key)] = source[key]; });
    });
    Object.keys(LEGACY_SECTION_KEYS).forEach(function (legacyKey) {
      var canonicalKey = LEGACY_SECTION_KEYS[legacyKey];
      if (campaign[legacyKey] != null && section.settings[canonicalKey] == null) section.settings[canonicalKey] = asOverride(campaign[legacyKey]);
    });
    return section;
  }

  function normaliseCampaignBlocks(campaign) {
    var sourceSection = asObject(campaign.section);
    var source = firstPopulatedObject([
      campaign.blocks, campaign.blockOverrides, campaign.block_overrides,
      campaign.contentBlocks, campaign.content_blocks, sourceSection.blocks
    ]);
    if (Array.isArray(source)) {
      var mapped = {};
      source.forEach(function (block, index) {
        var blockId = block && (block.id || block.blockId || block.block_id) || String(index);
        mapped[blockId] = block || {};
      });
      return mapped;
    }
    return source || {};
  }

  function revealCampaignShell() {
    if (window.ExseedDlpRevealTimer) window.clearTimeout(window.ExseedDlpRevealTimer);
    document.documentElement.classList.remove('exseed-dlp-pending');
  }

  function init(config) {
    if (!config || config.dataset.initialised === 'true') return;
    config.dataset.initialised = 'true';
    var payload;
    try { payload = JSON.parse(config.textContent || 'null'); } catch (error) { revealCampaignShell(); return; }
    if (typeof payload === 'string') { try { payload = JSON.parse(payload); } catch (error) { revealCampaignShell(); return; } }
    if (!payload || !Array.isArray(payload.campaigns)) { revealCampaignShell(); return; }
    var parameter = config.getAttribute('data-parameter') || 'campaign';
    var slug = (new URLSearchParams(window.location.search).get(parameter) || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(slug)) { revealCampaignShell(); return; }
    var campaign = payload.campaigns.find(function (item) { return item && item.enabled !== false && item.slug === slug; });
    if (!campaign) { revealCampaignShell(); return; }
    var sectionId = config.id.replace('ExseedDynamicPDPConfig-', '');
    var shell = document.getElementById('ExseedPDPShell-' + sectionId);
    if (!shell) { revealCampaignShell(); return; }
    shell.setAttribute('data-active-campaign', slug);
    var campaignSection = normaliseCampaignSection(campaign);
    function applyCampaign() {
      applySection(shell, campaignSection);
      applyBlocks(shell, normaliseCampaignBlocks(campaign), campaignSection);
      applySeo(campaign);
      shell.querySelectorAll('product-form').forEach(function (form) {
        var cta = asOverride(campaignSection.settings.add_to_cart_text);
        var soldOut = asOverride(campaignSection.settings.sold_out_text);
        if (cta && cta.value) form.setAttribute('data-add-to-cart-text', cta.value);
        if (soldOut && soldOut.value) form.setAttribute('data-sold-out-text', soldOut.value);
      });
    }
    applyCampaign();
    revealCampaignShell();
    [100, 500, 1500, 3000].forEach(function (delay) { window.setTimeout(applyCampaign, delay); });
  }

  document.querySelectorAll('[data-exseed-dynamic-pdp-config]').forEach(init);
  document.addEventListener('shopify:section:load', function (event) {
    var config = event.target && event.target.querySelector('[data-exseed-dynamic-pdp-config]');
    if (config) init(config);
  });
})();
